import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from 'redux/actions/index';
import { Icon, ICONS } from 'components/Icons';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import { compareSourceNames } from 'helpers/sourceSort';

const CATEGORY_ORDER = [
  'SCHOOL SUBSCRIPTIONS',
  'FREE SOURCES',
  'LOCAL SOURCES',
  'OTHER FREE SOURCES',
];

function categorizeSource(source) {
  if (source.subscriptionStatus === 'SchoolSubscription') {
    return 'SCHOOL SUBSCRIPTIONS';
  }
  const scope = (source.geographicScope || '').toLowerCase();
  if (scope === 'local' || scope === 'regional') {
    return 'LOCAL SOURCES';
  }
  if (source.subscriptionStatus === 'Free') {
    return 'FREE SOURCES';
  }
  return 'OTHER FREE SOURCES';
}

function SourceChip({ label, onRemove, onAdd }) {
  if (onRemove) {
    return (
      <div className="source-chip source-chip-selected">
        <span>{label}</span>
        <button type="button" className="source-chip-remove" onClick={onRemove}>
          <Icon icon={ICONS.CLOSE} />
        </button>
      </div>
    );
  }
  return (
    <div className="source-chip source-chip-add" onClick={onAdd}>
      + {label}
    </div>
  );
}

function SyllabusUploadStepPreferredSources({
  value,
  onChange,
  onBack,
  onConfirm,
  loading,
  courseId,
  courseSources,
  actions,
  openCreateCourseSourceModal,
  mode,
  syllabus,
}) {
  const initialSources = useRef(value.sources ? [...value.sources] : []);
  const [allSources, setAllSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedIds = useMemo(
    () => new Set(value.sources || []),
    [value.sources],
  );

  // Sources already covered by a school subscription shouldn't also show
  // up (and be addable again) under FREE/LOCAL/OTHER FREE SOURCES - they're
  // already listed under SCHOOL SUBSCRIPTIONS above.
  const schoolSubscriptionIds = useMemo(
    () =>
      new Set(
        (courseSources || [])
          .filter((s) => s.subscriptionStatus === 'SchoolSubscription')
          .map((s) => s.sourceId),
      ),
    [courseSources],
  );

  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const [response] = await Promise.all([
          actions.getSources({
            sourceQueryText: '',
            geographicScopes: 'national,regional,local',
            location: 'All',
            pageSize: 3000,
          }),
          courseSources === undefined
            ? actions.getCourseSources(courseId)
            : Promise.resolve(),
        ]);
        const normalized = (response?.sources || [])
          .filter((source) =>
            ['approved', 'limitedToCourse'].includes(
              source.sourceApprovalStatus,
            ),
          )
          .map((source) => ({
            id: source._id || source.sourceId || source.id || source.sourceName,
            name: source.sourceName || source.name || '',
            subscriptionStatus: source.subscriptionStatus || '',
            geographicScope: source.geographicScope || '',
          }))
          .filter(
            (s, i, arr) => s.id && arr.findIndex((x) => x.id === s.id) === i,
          )
          .sort((a, b) => compareSourceNames(a.name, b.name));
        setAllSources(normalized);
      } catch (error) {
        console.error('Error fetching sources:', error);
      } finally {
        setLoadingSources(false);
      }
    };
    fetchSources();
  }, [actions, courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSource = useCallback(
    (id) => {
      const current = value.sources || [];
      const next = selectedIds.has(id)
        ? current.filter((s) => s !== id)
        : [...current, id];
      onChange({ ...value, sources: next });
    },
    [value, onChange, selectedIds],
  );

  const grouped = useMemo(() => {
    const map = {};
    CATEGORY_ORDER.forEach((cat) => {
      map[cat] = { selected: [], total: 0 };
    });
    allSources.forEach((source) => {
      if (schoolSubscriptionIds.has(source.id)) return;
      const cat = categorizeSource(source);
      if (!map[cat]) map[cat] = { selected: [], total: 0 };
      map[cat].total++;
      if (selectedIds.has(source.id)) {
        map[cat].selected.push(source);
      }
    });
    return map;
  }, [allSources, selectedIds, schoolSubscriptionIds]);

  const unselected = useMemo(
    () =>
      allSources.filter(
        (s) => !selectedIds.has(s.id) && !schoolSubscriptionIds.has(s.id),
      ),
    [allSources, selectedIds, schoolSubscriptionIds],
  );

  const suggestions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return unselected.filter((s) => s.name.toLowerCase().includes(query));
  }, [unselected, searchQuery]);

  const activeCategories = CATEGORY_ORDER.filter((cat) => {
    if (cat === 'SCHOOL SUBSCRIPTIONS') return true;
    return grouped[cat]?.selected.length > 0;
  });

  return (
    <div className="syllabus-step">
      <div className="main-con">
        <div className="modal-header">
          <div className="syllabus-step-title">
            {syllabus?.settings?.sources?.length > 0
              ? 'Update your preferred sources for news recommendations'
              : 'Choose your preferred sources for news recommendations'}
          </div>
          <p className="syllabus-step-desc mt-1">
            We'll search these sources for suggested articles each week. Remove
            any that don't fit your class.
          </p>
        </div>
        <div className="section-con">
          {loadingSources ? (
            <div className="syllabus-preference-help">Loading sources...</div>
          ) : (
            <>
              {activeCategories.map((cat, idx) => (
                <div key={cat} className="source-category-group">
                  {idx > 0 && <div className="source-category-divider" />}
                  <div className="source-category-header">
                    <span className="source-category-title">{cat}</span>
                  </div>
                  {cat === 'SCHOOL SUBSCRIPTIONS' ? (
                    <>
                      {(courseSources || []).length > 0 && (
                        <div className="source-chips-row mb-2">
                          {(courseSources || [])
                            .filter(
                              (i) =>
                                i.subscriptionStatus === 'SchoolSubscription',
                            )
                            .map((source) => (
                              <SourceChip
                                key={source._id}
                                label={source.sourceName || source.name || ''}
                              />
                            ))}
                        </div>
                      )}
                      {(courseSources || []).length === 0 ? (
                        <p className="source-setup-empty d-flex align-items-center">
                          <Icon
                            icon={ICONS.DANGER_WARNING}
                            className="av-orange mr-2"
                          />
                          {"You haven't set up your school subscriptions. "}
                          <ActionLink
                            onClick={() =>
                              openCreateCourseSourceModal(courseId)
                            }
                            linkClassName="source-setup-link ml-2"
                          >
                            Set up now
                          </ActionLink>
                          {'.'}
                        </p>
                      ) : (
                        <p className="source-setup-empty d-flex align-items-center">
                          {'Manage your '}
                          <ActionLink
                            onClick={() =>
                              openCreateCourseSourceModal(courseId)
                            }
                            linkClassName="source-setup-link ml-2"
                          >
                            school subscriptions
                          </ActionLink>
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="source-chips-row">
                      {grouped[cat].selected.map((source) => (
                        <SourceChip
                          key={source.id}
                          label={source.name}
                          onRemove={() => toggleSource(source.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="source-add-section">
                <div className="source-add-title">Add more sources</div>
                <div className="source-search-wrap">
                  <span className="source-search-icon">
                    <Icon icon={ICONS.SEARCH} />
                  </span>
                  <input
                    className="source-search-input"
                    type="text"
                    placeholder="Search sources..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="source-chips-row mt-2">
                  {suggestions.map((source) => (
                    <SourceChip
                      key={source.id}
                      label={source.name}
                      onAdd={() => toggleSource(source.id)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="syllabus-step-footer">
          <div />
          <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
            <RectangularButton onClick={onBack} disabled={loading}>
              {mode === 'sources' ? 'Cancel' : 'Back'}
            </RectangularButton>
            <RectangularButton
              darkButton
              onClick={onConfirm}
              disabled={
                loading ||
                loadingSources ||
                (mode === 'sources' &&
                  (value.sources || []).length ===
                    initialSources.current.length &&
                  (value.sources || []).every((id) =>
                    initialSources.current.includes(id),
                  ))
              }
            >
              {mode === 'sources' ? 'Save' : 'Continue'}
            </RectangularButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
    openCreateCourseSourceModal: (courseId) =>
      dispatch(
        Actions.openModal('create-course-source', {
          courseId,
          schoolSubscriptionsMode: true,
        }),
      ),
  };
}

function mapStateToProps(state, ownProps) {
  return {
    courseSources: state.courseSources[ownProps.courseId],
    syllabus: state.syllabus?.currentSyllabus?.syllabus || null,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SyllabusUploadStepPreferredSources);
